import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../../hooks/useApi';
import { listTournaments } from '../../lib/api';
import { DataTable } from '../../components/DataTable';
import { PS, T } from '../../lib/theme';
import { formatClock } from '../../lib/time';
import { feeGelFromMinor, tournamentStatusColor, tournamentStatusLabel } from '../../lib/tournaments';
import { formatGel } from '../../lib/format';
import type { AdminTournament } from '../../lib/types';
import { compareDateTimeDescending, orderByDateTimeDescending } from '../../lib/admin-list-order';

const actionBase: CSSProperties = {
  padding: '8px 16px',
  borderRadius: 3,
  minHeight: 40,
  display: 'inline-flex',
  alignItems: 'center',
  ...T.buttonLabel,
  textTransform: 'uppercase',
  textDecoration: 'none',
};

const primaryAction: CSSProperties = { ...actionBase, background: PS.red, color: PS.white };
const secondaryAction: CSSProperties = {
  ...actionBase,
  background: PS.white,
  color: PS.nearBlack,
  border: `1px solid ${PS.border}`,
};

const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'past', label: 'Past' },
] as const;

type Tab = (typeof TABS)[number]['key'];

export function TournamentsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('active');
  const loader = useCallback((signal: AbortSignal) => listTournaments(signal), []);
  const tournaments = useApi<AdminTournament[]>(loader);
  const refetchTournaments = tournaments.refetch;

  const visible = useMemo(() => orderByDateTimeDescending(
    (tournaments.data ?? []).filter((tour) => tour.is_past === (tab === 'past')),
    (tour) => ({ date: tour.date, time: tour.start_time, id: tour.id }),
  ), [tournaments.data, tab]);

  useEffect(() => {
    const refresh = () => void refetchTournaments();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    const interval = window.setInterval(refresh, 60_000);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(interval);
    };
  }, [refetchTournaments]);

  const columns = useMemo<ColumnDef<AdminTournament, unknown>[]>(
    () => [
      { header: 'ID', accessorKey: 'id', cell: (info) => `#${info.getValue<number>()}` },
      {
        header: 'Title',
        // `en` first for the admin list — the admin UI itself is English-only
        // (CLAUDE.md), so this is just which locale's copy identifies a row,
        // not a claim about which locale matters more to players.
        accessorFn: (row) => row.translations.en?.title ?? row.title,
        id: 'title',
      },
      {
        header: 'Date',
        accessorKey: 'date',
        sortingFn: (left, right) => -compareDateTimeDescending(
          { date: left.original.date, time: left.original.start_time, id: left.original.id },
          { date: right.original.date, time: right.original.start_time, id: right.original.id },
        ),
        sortUndefined: 'last',
      },
      {
        header: 'Time',
        accessorFn: (row) => `${formatClock(row.start_time)}–${formatClock(row.end_time)}`,
        id: 'time',
      },
      {
        header: 'Status',
        id: 'status',
        cell: (info) => <StatusBadge status={info.row.original.status} />,
      },
      {
        header: 'Fee',
        accessorFn: (row) => formatGel(feeGelFromMinor(row.fee_minor)),
        id: 'fee',
      },
      {
        header: 'Capacity',
        accessorFn: (row) => (row.max_participants != null ? row.max_participants : 'Unlimited'),
        id: 'capacity',
      },
    ],
    [],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>Tournaments</h1>
        <Link to="/tournaments/new" style={primaryAction}>
          New tournament
        </Link>
        <Link to="/tournaments/series" style={secondaryAction}>
          Series →
        </Link>
      </header>

      <nav style={{ display: 'flex', gap: 8 }}>
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
            style={tab === key ? { ...actionBase, background: PS.nearBlack, color: PS.white } : secondaryAction}
          >
            {label}
          </button>
        ))}
      </nav>

      {tournaments.loading && <div style={{ ...T.caption, color: PS.textSec }}>Loading…</div>}

      {tournaments.error && (
        <div
          style={{
            padding: 12,
            background: PS.errorBg,
            border: `1px solid ${PS.errorBorder}`,
            color: PS.errorText,
            borderRadius: 3,
          }}
        >
          {tournaments.error.message}
        </div>
      )}

      {!tournaments.loading && !tournaments.error && (
        <DataTable
          // Remount on tab switch so each tab starts from its own date order
          // (DataTable keeps sorting as internal state after mount).
          key={tab}
          columns={columns}
          data={visible}
          emptyLabel={tab === 'active' ? 'No active tournaments.' : 'No past tournaments.'}
          initialSort={[{ id: 'date', desc: true }]}
          onRowClick={(row) => navigate(`/tournaments/${row.id}`)}
          getRowLabel={(row) => `Open tournament ${row.title || row.id}`}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: AdminTournament['status'] }) {
  const color = tournamentStatusColor(status);
  return (
    <span
      style={{
        ...T.microLabel,
        color,
        background: PS.white,
        border: `1px solid ${color}`,
        borderRadius: 3,
        padding: '2px 8px',
      }}
    >
      {tournamentStatusLabel(status)}
    </span>
  );
}
